// Search: one box that finds tasks, notes, projects, areas, resources, people
// and captures, both by a case-insensitive substring of their text and by
// meaning, in one ranked list.

import { captureTitle, filedItem, latestClassification } from './today.ts';
import type { Area, Capture, Classification, ContainerKind, ItemKind, Note, Person, Project, Resource, Task } from './types';

/** What search can find. */
export type SearchType = ItemKind | ContainerKind | 'person' | 'capture';

/** Every searchable type, in the order the filters show and equal-ranked results sort. */
export const SEARCH_TYPES: readonly SearchType[] = ['task', 'note', 'project', 'area', 'resource', 'person', 'capture'];

/**
 * What search looks in until the person chooses otherwise: everything but
 * captures. A capture is the raw thing said or typed; once filed it is the
 * task or note it became, which is found as itself. Captures stay findable
 * by checking their filter (Chris, 2026-10-05: "since we have the ability to
 * filter, we should use that").
 */
export const SEARCH_DEFAULT_TYPES: readonly SearchType[] = SEARCH_TYPES.filter((type) => type !== 'capture');

export const SEARCH_TYPE_LABELS: Record<SearchType, string> = {
  task: 'Task',
  note: 'Note',
  project: 'Project',
  area: 'Area',
  resource: 'Resource',
  person: 'Person',
  capture: 'Capture',
};

export const SEARCH_TYPE_PLURALS: Record<SearchType, string> = {
  task: 'Tasks',
  note: 'Notes',
  project: 'Projects',
  area: 'Areas',
  resource: 'Resources',
  person: 'People',
  capture: 'Captures',
};

/**
 * A regular expression matching `query` literally: every character that is
 * not a letter, digit or space is escaped, so `.`, `*`, `%` and the like match
 * themselves. (A backslash before a non-alphanumeric character is that
 * character in a Postgres regular expression.)
 */
export function literalRegex(query: string): string {
  return query.trim().replace(/[^\p{L}\p{N}\s]/gu, (c) => `\\${c}`);
}

/**
 * A PostgREST `or` filter (for supabase-js `.or()`) matching rows where any of
 * `columns` contains `query`, ignoring case (`imatch`, Postgres `~*`). It is
 * a regular expression rather than ILIKE because PostgREST turns every `*` in
 * an ILIKE pattern into a wildcard, with no escape. Each value is
 * double-quoted, so commas and parentheses are text, not filter syntax.
 */
export function containsAny(columns: readonly string[], query: string): string {
  const quoted = `"${literalRegex(query).replace(/[\\"]/g, (c) => `\\${c}`)}"`;
  return columns.map((column) => `${column}.imatch.${quoted}`).join(',');
}

const SNIPPET_RADIUS = 60;

/**
 * One line of `text` for a result: around the first match of `query` when it
 * has one, otherwise its start. Null when there is no text.
 */
export function searchSnippet(text: string | null | undefined, query: string, radius = SNIPPET_RADIUS): string | null {
  const line = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!line) return null;
  const needle = query.trim().toLowerCase();
  const at = needle ? line.toLowerCase().indexOf(needle) : -1;
  const start = at < 0 ? 0 : Math.max(0, at - radius);
  const end = at < 0 ? Math.min(line.length, 2 * radius) : Math.min(line.length, at + needle.length + radius);
  return `${start > 0 ? '…' : ''}${line.slice(start, end).trim()}${end < line.length ? '…' : ''}`;
}

/** How well a title matches: 0 is the whole title, 1 its start, 2 inside it, 3 not in the title (only in the text). */
export function titleMatchRank(title: string, query: string): number {
  const t = title.trim().toLowerCase();
  const q = query.trim().toLowerCase();
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  if (t.includes(q)) return 2;
  return 3;
}

// Blended ranking. The one search box returns two kinds of match together:
// rows whose text contains the query, and rows the database found nearest to
// the query's meaning (a vector similarity). Both halves are scored on the
// same 0..1 scale so they can be compared, and a hit keeps the better of its
// two scores: a strong match either way ranks high. Agreement — the same item
// found both ways — adds a small bonus, so it wins a tie against an item only
// one half found.

/** The text half's score for a hit whose whole title is the query. Such hits are put first outright; this keeps the scale complete. */
export const TEXT_SCORE_WHOLE_TITLE = 1;
/** The text half's score for a title that starts with the query. */
export const TEXT_SCORE_TITLE_STARTS = 0.9;
/** The text half's score for a title that contains the query elsewhere. */
export const TEXT_SCORE_TITLE_CONTAINS = 0.8;
/** The text half's score for a hit whose body text matched but whose title did not. */
export const TEXT_SCORE_TEXT_ONLY = 0.7;
/** Added when text and meaning both found the same item, so agreement outranks a lone half at the same score. */
export const AGREEMENT_BONUS = 0.05;

const TEXT_SCORES = [TEXT_SCORE_WHOLE_TITLE, TEXT_SCORE_TITLE_STARTS, TEXT_SCORE_TITLE_CONTAINS, TEXT_SCORE_TEXT_ONLY];

/** What ranking needs of a result: its kind, its identity, and its title. */
export interface RankableSearchHit {
  type: SearchType;
  id: string;
  title: string;
}

/**
 * One half's view of one result: the hit itself, whether the text search
 * returned it, and the meaning similarity (0..1) when the meaning search
 * returned it. The same item can arrive as two candidates, one per half.
 */
export interface SearchCandidate<T extends RankableSearchHit> {
  hit: T;
  byText: boolean;
  similarity: number | null;
}

/** How a result is identified across both halves, and in a list of results. */
export function searchHitKey(hit: Pick<RankableSearchHit, 'type' | 'id'>): string {
  return `${hit.type}-${hit.id}`;
}

/** The text half's score for a hit on the 0..1 scale; 0 when the text search did not return it at all. */
export function textSearchScore(candidate: SearchCandidate<RankableSearchHit>, query: string): number {
  return candidate.byText ? TEXT_SCORES[titleMatchRank(candidate.hit.title, query)] : 0;
}

/** The better of a hit's two halves, plus the bonus when both halves found it. */
export function blendedSearchScore(candidate: SearchCandidate<RankableSearchHit>, query: string): number {
  const best = Math.max(textSearchScore(candidate, query), candidate.similarity ?? 0);
  return best + (candidate.byText && candidate.similarity !== null ? AGREEMENT_BONUS : 0);
}

function bestSimilarity(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

/**
 * One candidate per item: the text half's hit is the one kept, because it
 * carries the snippet and marks the text search built, and the meaning half's
 * similarity is carried across.
 */
function mergeSearchCandidates<T extends RankableSearchHit>(candidates: readonly SearchCandidate<T>[]): SearchCandidate<T>[] {
  const merged = new Map<string, SearchCandidate<T>>();
  for (const candidate of candidates) {
    const key = searchHitKey(candidate.hit);
    const seen = merged.get(key);
    merged.set(
      key,
      seen
        ? {
            hit: seen.byText ? seen.hit : candidate.hit,
            byText: seen.byText || candidate.byText,
            similarity: bestSimilarity(seen.similarity, candidate.similarity),
          }
        : candidate,
    );
  }
  return [...merged.values()];
}

/**
 * Results best first, each item once: titles that are exactly the query, then
 * everything else by its blended score, then by type order and title. Merging
 * happens here, so an item found by both halves appears once with both halves'
 * evidence behind its place in the list.
 */
export function rankSearchHits<T extends RankableSearchHit>(candidates: readonly SearchCandidate<T>[], query: string): T[] {
  return mergeSearchCandidates(candidates)
    .map((candidate) => ({
      hit: candidate.hit,
      exact: titleMatchRank(candidate.hit.title, query) === 0 ? 0 : 1,
      score: blendedSearchScore(candidate, query),
      order: SEARCH_TYPES.indexOf(candidate.hit.type),
    }))
    .sort((a, b) => a.exact - b.exact || b.score - a.score || a.order - b.order || a.hit.title.localeCompare(b.hit.title))
    .map(({ hit }) => hit);
}

// The search itself. Both apps run this one implementation; each supplies the
// reads (SearchIo, from its own Supabase client, as the signed-in user) and
// turns a hit's target into its own route.

/** The most rows each type returns; a type that reaches it says there are more. */
export const SEARCH_LIMIT = 25;

/** The most meaning matches the database returns for one search, across every type together. */
export const MEANING_LIMIT = 20;

/**
 * Where a result opens, in terms both apps share. A filed capture opens the task
 * or note it became; an unfiled capture opens its own filing view.
 */
export type SearchTarget = { kind: ItemKind | ContainerKind | 'person' | 'capture'; id: string };

/** One search result: what it is, what it is called, a line of its text, and where it opens. */
export interface SearchHit {
  type: SearchType;
  id: string;
  title: string;
  snippet: string | null;
  target: SearchTarget;
  /** Short status words shown beside it: "Archived", "Done", "Not filed yet". */
  marks: string[];
}

export interface SearchResults {
  hits: SearchHit[];
  /** Types that returned SEARCH_LIMIT rows, so more of them match than are shown. */
  fullTypes: SearchType[];
  /**
   * Why the meaning half could not run, so these are text matches only; null
   * when it ran. The app says so beside the results.
   */
  meaningFailure: string | null;
}

/** What the search_embeddings RPC is sent. */
export interface SearchEmbeddingsArgs {
  p_query_embedding: number[];
  p_model: string;
  p_limit: number;
  p_item_types: SearchType[];
}

/** The reads one search makes, each as the signed-in user, so row-level security decides what exists to be found. */
export interface SearchIo {
  /** At most `limit` rows of `table`, with the columns in `select`, that pass the PostgREST `or` filter `filter`. */
  matchingRows(table: string, select: string, filter: string, limit: number): Promise<unknown[]>;
  /** The rows of `table` with these ids, with the columns in `select`. */
  rowsById(table: string, select: string, ids: readonly string[]): Promise<unknown[]>;
  /** The semantic-search edge function's answer to `{ query }`; it should be `{ model, embedding }`. */
  semanticSearch(query: string): Promise<unknown>;
  /** The search_embeddings RPC's answer; it should be rows of `{ item_type, item_id, similarity }`. */
  searchEmbeddings(args: SearchEmbeddingsArgs): Promise<unknown>;
}

/** A capture row with its embedded classifications, as `select('*, classifications(*)')` returns it. */
type CaptureRow = Capture & { classifications: readonly Classification[] | null };

interface Source {
  table: string;
  select: string;
  /** The text columns the query is looked for in; the first is the title. */
  columns: readonly string[];
  /** The result a row makes, or null for a row that is not to be found (a capture that has been filed). */
  hit: (row: never, query: string) => SearchHit | null;
}

const archived = (row: { archived_at: string | null }) => (row.archived_at ? ['Archived'] : []);

const container = (type: 'area' | 'resource'): Source => ({
  table: type === 'area' ? 'areas' : 'resources',
  select: 'id, name, description, archived_at',
  columns: ['name', 'description'],
  hit: (row: Area | Resource, query: string): SearchHit => ({
    type,
    id: row.id,
    title: row.name,
    snippet: searchSnippet(row.description, query),
    target: { kind: type, id: row.id },
    marks: archived(row),
  }),
});

// What each type searches, using the columns the apps show for it. Archived
// containers are included and marked, as All tasks and container chips do.
const SOURCES: Record<SearchType, Source> = {
  task: {
    table: 'tasks',
    select: 'id, name, notes, status',
    columns: ['name', 'notes'],
    hit: (row: Task, query) => ({
      type: 'task',
      id: row.id,
      title: row.name,
      snippet: searchSnippet(row.notes, query),
      target: { kind: 'task', id: row.id },
      marks: row.status === 'done' ? ['Done'] : [],
    }),
  },
  note: {
    table: 'notes',
    select: 'id, title, body',
    columns: ['title', 'body'],
    hit: (row: Note, query) => ({
      type: 'note',
      id: row.id,
      title: row.title,
      snippet: searchSnippet(row.body, query),
      target: { kind: 'note', id: row.id },
      marks: [],
    }),
  },
  project: {
    table: 'projects',
    select: 'id, name, archived_at',
    columns: ['name'],
    hit: (row: Project) => ({
      type: 'project',
      id: row.id,
      title: row.name,
      snippet: null,
      target: { kind: 'project', id: row.id },
      marks: archived(row),
    }),
  },
  area: container('area'),
  resource: container('resource'),
  person: {
    table: 'people',
    select: 'id, name, context',
    columns: ['name', 'context'],
    hit: (row: Person, query) => ({
      type: 'person',
      id: row.id,
      title: row.name,
      snippet: searchSnippet(row.context, query),
      target: { kind: 'person', id: row.id },
      marks: [],
    }),
  },
  capture: {
    table: 'captures',
    select: '*, classifications(*)',
    columns: ['raw_text'],
    // Captures are looked in only when their filter is checked (SEARCH_DEFAULT_TYPES leaves it
    // off). A filed one opens the task or note it became; one still in the Inbox opens to be filed.
    hit: (row: CaptureRow, query) => {
      const item = filedItem(latestClassification(row));
      const text = row.raw_text.trim();
      return {
        type: 'capture',
        id: row.id,
        title: captureTitle(row.raw_text),
        snippet: searchSnippet(text.slice(text.split('\n')[0].length), query),
        target: item ?? { kind: 'capture', id: row.id },
        marks: item ? ['Filed'] : ['Not filed yet'],
      };
    },
  },
};

async function searchType(io: SearchIo, type: SearchType, query: string): Promise<{ type: SearchType; hits: SearchHit[]; full: boolean }> {
  const source = SOURCES[type];
  const rows = (await io.matchingRows(source.table, source.select, containsAny(source.columns, query), SEARCH_LIMIT)) as never[];
  return { type, hits: rows.flatMap((row) => source.hit(row, query) ?? []), full: rows.length >= SEARCH_LIMIT };
}

/** One row of the meaning search: which item the database found near the query, and how near. */
interface MeaningMatch {
  type: SearchType;
  id: string;
  similarity: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** `value` when it is one of the searchable types, else null; the row's type decides which table it is read from. */
function asSearchType(value: unknown): SearchType | null {
  return SEARCH_TYPES.find((type) => type === value) ?? null;
}

/** `value` as a list of finite numbers, or null when it is anything else. An embedding is that and nothing else. */
function asNumbers(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  const numbers: number[] = [];
  for (const item of value) {
    if (typeof item !== 'number' || !Number.isFinite(item)) return null;
    numbers.push(item);
  }
  return numbers.length > 0 ? numbers : null;
}

/**
 * The items the database holds nearest the query's meaning, best first and
 * already cut to a minimum similarity by the database. The query is embedded
 * by the semantic-search edge function (the embedding provider is reached by
 * the server, never the app), and the model that made the vector comes back
 * with it, because only embeddings from the same model may be compared.
 * Throws when either call fails or answers something else.
 */
async function meaningMatches(io: SearchIo, query: string, types: SearchType[]): Promise<MeaningMatch[]> {
  const answer = await io.semanticSearch(query);
  const embedding = isRecord(answer) ? asNumbers(answer.embedding) : null;
  const model = isRecord(answer) && typeof answer.model === 'string' ? answer.model : '';
  if (!embedding || !model) throw new Error('semantic-search did not return an embedding');
  const data = await io.searchEmbeddings({ p_query_embedding: embedding, p_model: model, p_limit: MEANING_LIMIT, p_item_types: types });
  if (!Array.isArray(data)) throw new Error('search_embeddings did not return rows');
  return data.flatMap((row: unknown) => {
    if (!isRecord(row)) return [];
    const type = asSearchType(row.item_type);
    const { item_id: id, similarity } = row;
    if (!type || !types.includes(type) || typeof id !== 'string' || typeof similarity !== 'number') return [];
    return [{ type, id, similarity }];
  });
}

/**
 * The rows behind meaning matches, read per type by id and turned into hits by
 * the same mapping the text search uses, so a meaning match and a text match
 * are the same kind of result. `searchSnippet` falls back to the start of the
 * text, which is what a hit whose words are nowhere in the query should show.
 */
async function meaningHits(io: SearchIo, matches: readonly MeaningMatch[], query: string): Promise<SearchHit[]> {
  const byType = SEARCH_TYPES.map((type) => ({ type, ids: matches.filter((match) => match.type === type).map((match) => match.id) })).filter(
    ({ ids }) => ids.length > 0,
  );
  const found = await Promise.all(
    byType.map(async ({ type, ids }) => {
      const source = SOURCES[type];
      const rows = (await io.rowsById(source.table, source.select, ids)) as never[];
      return rows.flatMap((row) => source.hit(row, query) ?? []);
    }),
  );
  return found.flat();
}

/** What the meaning half contributed: hits the text search did not have, the similarity of every match, and why it failed, if it did. */
interface MeaningHalf {
  hits: SearchHit[];
  /** Similarity by searchHitKey, for text hits the meaning search also found as well as for meaning-only hits. */
  similarity: Map<string, number>;
  failure: string | null;
}

function reason(err: unknown): string {
  if (isRecord(err) && typeof err.message === 'string' && err.message.trim()) return err.message;
  return typeof err === 'string' && err.trim() ? err : 'unknown error';
}

/**
 * The meaning half of one search, from the started embedding lookup to the
 * rows behind the matches. It never throws: meaning search can be unavailable
 * — the provider is down, no key is configured, the network failed — and then
 * the search is text matches alone and carries the reason, rather than failing
 * or quietly looking like there is nothing to find.
 */
async function meaningHalf(io: SearchIo, started: Promise<MeaningMatch[]>, query: string, alreadyFound: ReadonlySet<string>): Promise<MeaningHalf> {
  try {
    const matches = await started;
    const missing = matches.filter((match) => !alreadyFound.has(searchHitKey(match)));
    return {
      hits: await meaningHits(io, missing, query),
      similarity: new Map(matches.map((match) => [searchHitKey(match), match.similarity])),
      failure: null,
    };
  } catch (err) {
    return { hits: [], similarity: new Map(), failure: reason(err) };
  }
}

/**
 * Every task, note, project, area, resource, person and capture of the
 * signed-in user that matches `query`, for the chosen types, in one ranked
 * list: rows whose text contains the query and rows whose meaning is near it,
 * each item once. A blank query, or no types, reads nothing. Throws when the
 * text search itself fails; a failure of the meaning half leaves the text
 * matches and sets `meaningFailure`.
 */
export async function searchItems(io: SearchIo, query: string, types: readonly SearchType[]): Promise<SearchResults> {
  const term = query.trim();
  if (!term || types.length === 0) return { hits: [], fullTypes: [], meaningFailure: null };
  const wanted = SEARCH_TYPES.filter((type) => types.includes(type));
  // Both halves start together; the meaning half is finished afterwards, once
  // the text hits are known, so only rows text search did not return are read.
  const started = meaningMatches(io, term, wanted);
  started.catch(() => undefined); // its failure is handled in meaningHalf, and is not the search failing
  const found = await Promise.all(wanted.map((type) => searchType(io, type, term)));
  const textHits = found.flatMap((f) => f.hits);
  const meaning = await meaningHalf(io, started, term, new Set(textHits.map(searchHitKey)));
  const candidates: SearchCandidate<SearchHit>[] = [
    ...textHits.map((hit) => ({ hit, byText: true, similarity: meaning.similarity.get(searchHitKey(hit)) ?? null })),
    ...meaning.hits.map((hit) => ({ hit, byText: false, similarity: meaning.similarity.get(searchHitKey(hit)) ?? null })),
  ];
  return {
    hits: rankSearchHits(candidates, term),
    fullTypes: found.filter((f) => f.full).map((f) => f.type),
    meaningFailure: meaning.failure,
  };
}
